import { z } from "zod";
import { IsoUtc, Uuid } from "./primitives";
import { StaffPermission, StaffRole } from "./permissions";

/**
 * Operational admin (M7 slice 1, ADR-0035; docs/06 section 7). Every route here is staff-only and checks one permission
 * on the server; nothing a browser sends (body, header, cookie, query) can claim a role or a permission.
 *   GET /admin/me      the caller's own staff roles and permissions (for the UI only; the server never trusts it)
 *   GET /admin/audit   the admin audit log, newest first, cursor pagination (audit.read)
 *   GET /admin/roles   current role assignments with grantor, reason and time (roles.read); grants stay CLI-only
 */

export const AdminMeResponse = z.object({
  userId: z.string(),
  displayName: z.string(),
  roles: z.array(StaffRole),
  permissions: z.array(StaffPermission),
});
export type AdminMeResponse = z.infer<typeof AdminMeResponse>;

/** Audit action filter: one action ("report.ban") or a family ("report" matches report.ban, report.dismiss, ...). */
export const AUDIT_ACTION_RE = /^[a-z0-9_]+(\.[a-z0-9_]+)*$/;

export const AdminAuditQuery = z.object({
  /** Exact admin id as stored: an account's user id, `cli:<operator>`, or the Townsy system account. */
  actor: z.string().trim().min(1).max(200).optional(),
  action: z.string().trim().max(80).regex(AUDIT_ACTION_RE, "letters, digits, _ and dots").optional(),
  /** Exact scope ("user:<id>", "campaign:<uuid>", "report:<uuid>"), or a bare id matching the part after the colon. */
  target: z.string().trim().min(1).max(200).optional(),
  /** From (inclusive) and to (exclusive), ISO UTC. */
  from: IsoUtc.optional(),
  to: IsoUtc.optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
});
export type AdminAuditQuery = z.infer<typeof AdminAuditQuery>;
export const ADMIN_AUDIT_DEFAULT_LIMIT = 50;

/** One admin_audit row exactly as stored (it is redacted when written), plus the actor's display name when known. */
export const AdminAuditEntry = z.object({
  auditId: Uuid,
  at: IsoUtc,
  actorId: z.string(),
  /** The account's display name; null for operator CLI ids (`cli:<name>`) and ids without a profile. */
  actorLabel: z.string().nullable(),
  action: z.string(),
  scope: z.string(),
  before: z.record(z.string(), z.unknown()).nullable(),
  after: z.record(z.string(), z.unknown()).nullable(),
  reason: z.string(),
  correlationId: z.string().nullable(),
});
export type AdminAuditEntry = z.infer<typeof AdminAuditEntry>;
export const AdminAuditResponse = z.object({ items: z.array(AdminAuditEntry), nextCursor: z.string().nullable() });
export type AdminAuditResponse = z.infer<typeof AdminAuditResponse>;

/** One active (unrevoked, unexpired) role assignment. */
export const RoleAssignmentView = z.object({
  assignmentId: Uuid,
  userId: z.string(),
  displayName: z.string().nullable(),
  /** Needed to revoke with the operator CLI (`revoke-role --email`). Shown to roles.read holders only. */
  email: z.string().nullable(),
  role: z.string(),
  /** The assignment grants permissions: a role in the matrix with the global scope "*" (anything else grants nothing). */
  known: z.boolean(),
  scope: z.string(),
  grantedBy: z.string().nullable(),
  reason: z.string(),
  grantedAt: IsoUtc,
  expiresAt: IsoUtc.nullable(),
});
export type RoleAssignmentView = z.infer<typeof RoleAssignmentView>;
export const RoleAssignmentsResponse = z.object({ items: z.array(RoleAssignmentView), truncated: z.boolean() });
export type RoleAssignmentsResponse = z.infer<typeof RoleAssignmentsResponse>;
export const MAX_ROLE_ASSIGNMENTS_LISTED = 500;
